import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {validInteger} from '../../validators/integer.validator';
import {debounceTime} from 'rxjs/operators';
import {isNil} from 'lodash-es';

@Component({
    selector: 'app-duration',
    templateUrl: './duration.component.html',
    styleUrls: ['./duration.component.scss'],
    standalone: false
})
export class DurationComponent implements OnInit {

  public form: FormGroup;
  hours: string;
  minutes: string;
  seconds: string;
  private _initialDuration: number;
  private _disabled = false;

  @Input()
  set initialDurationSeconds(initialDuration: number) {
    if (isNil(initialDuration)) {
      initialDuration = 0;
    }
    this._initialDuration = initialDuration;
    this.parseDuration(initialDuration);
  }

  @Input()
  set disabled(disabled: boolean) {
    this._disabled = disabled;
    if (!isNil(this.form)) {
      if (this._disabled) {
        this.form.controls.hours.disable();
        this.form.controls.minutes.disable();
        this.form.controls.seconds.disable();
      } else {
        this.form.controls.hours.enable();
        this.form.controls.minutes.enable();
        this.form.controls.seconds.enable();
      }
    }
  }

  @Output() durationChanged = new EventEmitter<number>();

  constructor(
    private fb: FormBuilder,
  ) {
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      hours: [{value: '00', disabled: this._disabled}, [Validators.required, Validators.min(0), validInteger]],
      minutes: [{value: '00', disabled: this._disabled}, [Validators.required, Validators.min(0), Validators.max(59)]],
      seconds: [{value: '00', disabled: this._disabled}, [Validators.required, Validators.min(0), Validators.max(59)]],
    });

    this.restrictControlValues('hours', 0);
    this.restrictControlValues('minutes', 0, 59);
    this.restrictControlValues('seconds', 0, 59);

    this.parseDuration(this._initialDuration);
  }

  private restrictControlValues(controlName: string, minValue: number, maxValue: number = null) {
    this.form.get(controlName).valueChanges
      .pipe(
        debounceTime(300),
      ).subscribe(value => {
        if (maxValue !== null && value > maxValue) {
          this.form.get(controlName).setValue(maxValue);
        }
      if (value < minValue)  {
        this.form.get(controlName).setValue(minValue);
      }
    });
  }


  onValueChanged() {
    this.hours = this.form.controls.hours.value?.toString() || 0;
    this.minutes = this.form.controls.minutes.value?.toString() || 0;
    this.seconds = this.form.controls.seconds.value?.toString() || 0;

    const updatedDuration = +this.hours * 60 * 60 + +this.minutes * 60 + +this.seconds;
    this.durationChanged.emit(updatedDuration);
  }

  private parseDuration(duration: number): void {
    const hours = Math.floor(duration / 60 / 60);
    const minutes = Math.floor((duration - hours * 60 * 60) / 60);
    const seconds = duration - hours * 60 * 60 - minutes * 60;

    this.hours = hours < 10 ? '0' + hours.toString() : hours.toString();
    this.minutes = minutes < 10 ? '0' + minutes.toString() : minutes.toString();
    this.seconds = seconds < 10 ? '0' + seconds.toString() : seconds.toString();

    if (!isNil(this.form)) {
      this.form.patchValue({
        hours: this.hours,
        minutes: this.minutes,
        seconds: this.seconds,
      });

      this.form.updateValueAndValidity();
    }

  }


  formatFields() {
    this.onValueChanged();

    if (isNil(this.hours)) {
      this.hours = '00';
    }
    if (isNil(this.minutes)) {
      this.minutes = '00';
    }
    if (isNil(this.seconds)) {
      this.seconds = '00';
    }

    if (this.hours.length < 2) {
      this.hours = '0' + this.hours;
    }
    if (this.minutes.length < 2) {
      this.minutes = '0' + this.minutes;
    }
    if (this.seconds.length < 2) {
      this.seconds = '0' + this.seconds;
    }

    this.form.patchValue({
      hours: this.hours,
      minutes: this.minutes,
      seconds: this.seconds,
    });

    this.form.updateValueAndValidity();

  }
}
